import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { SectionToggleInfo } from '@purespectrum1/ui/accordion';
import { EachNestingGroupData } from '../interfaces/multi-quota-nesting.interface';
import { MultiQuotaNestingService } from '../services/multi-quota-nesting.service';
import { NestingQualification } from '../../../shared/interfaces/quota-nesting.interface';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { filter, map, skip, switchMap, tap } from 'rxjs/operators';
import { NestedQuotaBuilder } from '../../domain/nested-quota-builder';
import { QuotaPayload } from '../../../shared/interfaces/quota.interface';
import { Constants as QuotasConstants } from '../../constants/quotas';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { notifyMessage } from '../../../shared/constants/notify-message';
import { ModalService } from '@purespectrum1/ui/modal';
import { ConfirmationModalComponent } from '../../../shared/ui/confirmation-modal/confirmation-modal.component';
import { SurveyNestedQuotasUpdater } from '../../domain/survey-nested-quotas-updater';
import { NestingGroupIdQuotaMatch } from '../../domain/quota-filter';
import { QuotaNestingTitleValidator } from '../validators/quota-nesting-title.validator';

@Component({
  selector: 'ps-ui-multi-quota-nesting-selection',
  templateUrl: './multi-quota-nesting-selection.component.html',
  styleUrls: ['./multi-quota-nesting-selection.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiQuotaNestingSelectionComponent implements OnInit, OnDestroy {
  @Input()
  public originalQuotas: QuotaPayload[] = [];

  @Input()
  public surveyAllocation: number = 0;

  public nestingGroupData$!: Observable<EachNestingGroupData[]>;
  public selectedNestingGroup$!: Observable<EachNestingGroupData>;

  private _subscriptions = new Subscription();
  private _nestingGroupData: EachNestingGroupData[] = [];
  private _nestingGroupSelectedSectionCommand$ =
    new BehaviorSubject<SectionToggleInfo>({ index: 0, opened: false });
  private _nestingQualificationSelectionCommand$ = new BehaviorSubject<{
    selectedNestingGroup: EachNestingGroupData;
    qualificationIndex: number;
    selected: boolean;
  }>({
    selectedNestingGroup: <EachNestingGroupData>{},
    qualificationIndex: 0,
    selected: false,
  });
  private _nestingGroupDeletionCommand$ =
    new BehaviorSubject<EachNestingGroupData>(<EachNestingGroupData>{});
  private _nestingGroupTitleUpdateCommand$ = new BehaviorSubject<{
    nestingGroup: EachNestingGroupData;
    newTitle: string;
  }>({ nestingGroup: <EachNestingGroupData>{}, newTitle: '' });

  constructor(
    private _multiQuotaNestingService: MultiQuotaNestingService,
    private _toaster: ToasterService,
    private _modal: ModalService
  ) {}

  public ngOnInit(): void {
    this._handleNestingGroupStreams();
    this._handleNestingGroupSelection();
    this._handleNestingGroupDeletion();
    this._handleNestingGroupTitleUpdate();
    this._handleNestingQualificationSelection();
  }

  private _handleNestingGroupStreams() {
    this.nestingGroupData$ = this._multiQuotaNestingService
      .getNestingGroupData()
      .pipe(tap((data) => (this._nestingGroupData = data)));
    this.selectedNestingGroup$ =
      this._multiQuotaNestingService.getSelectedNestingGroup();
  }

  private _handleNestingGroupSelection(): void {
    this._subscriptions.add(
      this._nestingGroupSelectedSectionCommand$
        .pipe(
          filter((selectedSection) => !!selectedSection.opened),
          filter(({ index }) => !!this._nestingGroupData[index]),
          map(({ index }) => this._nestingGroupData[index])
        )
        .subscribe((selected) => {
          this._multiQuotaNestingService.setSelectedNestingGroup(selected);
        })
    );
  }

  private _handleNestingQualificationSelection(): void {
    this._subscriptions.add(
      this._nestingQualificationSelectionCommand$
        .pipe(
          skip(1),
          map(({ selected, selectedNestingGroup, qualificationIndex }) => {
            selectedNestingGroup.qualifications[qualificationIndex].isChecked =
              selected;

            const { qualifications, selectedQualifications } =
              selectedNestingGroup;
            const updatedSelectedQualifications =
              this._rebuildSelectedQualificationsBasedOnSelection(
                selectedQualifications,
                qualifications[qualificationIndex]
              );
            return {
              ...selectedNestingGroup,
              selectedQualifications: updatedSelectedQualifications,
              quotas: new NestedQuotaBuilder(
                this.originalQuotas,
                updatedSelectedQualifications,
                this.surveyAllocation,
                selectedNestingGroup.id
              ).build(),
              canResetTable: false,
            };
          })
        )
        .subscribe((selectedNestingGroup) => {
          this._multiQuotaNestingService.setNestingGroupData(
            selectedNestingGroup.id,
            selectedNestingGroup
          );
        })
    );
  }

  private _handleNestingGroupDeletion(): void {
    this._subscriptions.add(
      this._nestingGroupDeletionCommand$
        .pipe(
          filter((nestingGroup) => {
            if (!nestingGroup || !nestingGroup.id) {
              return false;
            }
            return true;
          }),
          switchMap((nestingGroup) => this._deleteConfirmation(nestingGroup))
        )
        .subscribe((nestingGroup) => {
          const isLastCreatedGroupDeleting =
            this._nestingGroupData.length === 1;
          this._multiQuotaNestingService.deleteNestingGroupData(
            nestingGroup.id
          );
          if (isLastCreatedGroupDeleting) {
            this._multiQuotaNestingService.sendEmptySelectionStateCreationCommand();
          }
          this._multiQuotaNestingService.indicateUnsavedChanges();
        })
    );
  }

  private _handleNestingGroupTitleUpdate(): void {
    this._subscriptions.add(
      this._nestingGroupTitleUpdateCommand$
        .pipe(
          skip(1),
          switchMap((data) => this._nestingTitleValidations(data))
        )
        .subscribe(({ nestingGroup, newTitle }) => {
          const index = this._nestingGroupData.findIndex(
            (g) => g.id === nestingGroup.id
          );
          const updated: EachNestingGroupData = {
            ...nestingGroup,
            id: newTitle,
            titleState: 'view',
            quotas: new SurveyNestedQuotasUpdater(nestingGroup.quotas).update(
              new NestingGroupIdQuotaMatch(nestingGroup.id),
              { group_key: newTitle }
            ),
          };
          this._multiQuotaNestingService.replaceNestingGroupData(
            index,
            updated
          );
          this._multiQuotaNestingService.indicateUnsavedChanges();
        })
    );
  }

  private _deleteConfirmation(
    nestingGroup: EachNestingGroupData
  ): Observable<EachNestingGroupData> {
    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: `Do you want to delete the Quota Nesting named ${nestingGroup.id}?`,
        confirmButtonLabel: 'Confirm',
        cancelButtonLabel: 'Cancel',
      },
    });

    return modalRef.onClose$.pipe(
      filter((msg: string) => msg === 'ok'),
      map(() => nestingGroup)
    );
  }

  private _nestingTitleValidations({
    nestingGroup,
    newTitle,
  }: {
    nestingGroup: EachNestingGroupData;
    newTitle: string;
  }): Observable<{ nestingGroup: EachNestingGroupData; newTitle: string }> {
    return of(this._nestingGroupData).pipe(
      filter((nestingGroupData) => {
        const titleValidator = new QuotaNestingTitleValidator(newTitle);

        const { valid, message } = titleValidator.validate();

        if (!valid) {
          this._toaster.warning(message);
          return false;
        }

        const existingTitles = nestingGroupData
          .filter((g) => g.id !== nestingGroup.id)
          .map((g) => g.id);

        const { valid: duplicationValid, message: duplicationMessage } =
          titleValidator.validateDuplication(existingTitles);

        if (!duplicationValid) {
          this._toaster.warning(duplicationMessage);
          return false;
        }

        return true;
      }),
      map(() => ({ nestingGroup, newTitle }))
    );
  }

  public onDeleteNestedGroup(
    nestingGroup: EachNestingGroupData,
    event: Event
  ): void {
    event.stopPropagation();
    this._nestingGroupDeletionCommand$.next(nestingGroup);
  }

  public onEditNestedGroupTitle(
    nestingGroup: EachNestingGroupData,
    event: Event
  ): void {
    event.stopPropagation();
    const updated: EachNestingGroupData = {
      ...nestingGroup,
      titleState: 'edit',
    };
    this._multiQuotaNestingService.setNestingGroupData(updated.id, updated);
  }

  public onGroupSelection(groupSectionInfo: SectionToggleInfo): void {
    this._nestingGroupSelectedSectionCommand$.next(groupSectionInfo);
  }

  public validateQualificationSelectionLimit(
    selectedNestingGroup: EachNestingGroupData,
    event: Event
  ): void {
    const selected = (event.target as HTMLInputElement).checked;
    if (
      selected &&
      this._hasHitTheNestedQuotaLimits(
        selectedNestingGroup.selectedQualifications
      )
    ) {
      event.preventDefault();
      this._toaster.warning(
        notifyMessage.warningMessage.QUOTA.MAXIMUM_QUOTAS_TO_NEST.replace(
          ':NESTED_QUOTA_LIMIT',
          `${QuotasConstants.NESTED_QUOTA_LIMIT}`
        )
      );
      return;
    }
  }

  private _hasHitTheNestedQuotaLimits(
    selectedQualifications: NestingQualification[]
  ): boolean {
    return selectedQualifications.length >= QuotasConstants.NESTED_QUOTA_LIMIT;
  }

  public onQualificationSelection(
    selectedNestingGroup: EachNestingGroupData,
    qualificationIndex: number,
    event: Event
  ): void {
    const selected = (event.target as HTMLInputElement).checked;
    this._nestingQualificationSelectionCommand$.next({
      selectedNestingGroup,
      qualificationIndex,
      selected,
    });
    this._multiQuotaNestingService.indicateUnsavedChanges();
  }

  private _rebuildSelectedQualificationsBasedOnSelection(
    selectedQualifications: NestingQualification[],
    selection: NestingQualification
  ): NestingQualification[] {
    if (selection.isChecked) {
      return [...selectedQualifications, selection];
    }
    return selectedQualifications.filter(
      (qualification) =>
        Number(qualification.qualification_code) !==
        selection.qualification_code
    );
  }

  public confirmNestingTitleUpdate(
    nestingGroup: EachNestingGroupData,
    newTitle: string
  ) {
    this._nestingGroupTitleUpdateCommand$.next({
      nestingGroup,
      newTitle: newTitle.trim(),
    });
  }

  public discardNestingTitleUpdate(nestingGroup: EachNestingGroupData) {
    const updated: EachNestingGroupData = {
      ...nestingGroup,
      titleState: 'view',
    };
    this._multiQuotaNestingService.setNestingGroupData(updated.id, updated);
  }

  public trackByGroup(
    _index: number,
    nestingGroup: EachNestingGroupData
  ): string {
    return nestingGroup.uniqueId;
  }

  public trackByQualification(
    _index: number,
    qualification: NestingQualification
  ): number {
    return qualification.qualification_code;
  }

  public ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
